'use client';

import { type FC, forwardRef } from 'react';

import ReactSlider from 'react-slider';

import { Typography } from '../Typography/Typography';
import { DEFAULT_LOCALE } from './Slider.constants';
import type { SliderProps, ValueState } from './Slider.types';

import styles from './Styles/Default.module.css';

export const Slider: FC<SliderProps> = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      dataTestId,
      className,
      onChange,
      onAfterChange,
      locale = DEFAULT_LOCALE,
      minDistance,
      isSingleKnob,
      stepCount,
      step,
      selectedRangeIndexes,
      ariaValueText,
      isWithHistogram,
      disabled = false,
      ...rest
    },
    ref,
  ) => {
    function removeDraggingClass() {
      const activeThumb = document.getElementsByClassName('dragging')[0] as HTMLDivElement;

      if (activeThumb) activeThumb.blur();
    }

    const singleKnobChange = (
      value: number | number[],
      thumbIndex: number,
      onChange?: (value: [number, number], thumbIndex: number) => void,
    ) => {
      if (onChange) {
        const [value1, value2] = Array.isArray(value)
          ? value
          : [value, selectedRangeIndexes?.[1] || 0];
        onChange([value1, value2], thumbIndex);
      }
    };
    const singleKnobAfterChange = (
      values: number | number[],
      thumbIndex: number,
      onAfterChange?: (value: [number, number], thumbIndex: number) => void,
    ) => {
      removeDraggingClass();
      if (onAfterChange) {
        const [value1, value2] = Array.isArray(values)
          ? values
          : [values, selectedRangeIndexes?.[1] || 0];
        onAfterChange([value1, value2], thumbIndex);
      }
    };
    const commonSliderProps = {
      ariaValuetext: ariaValueText,
      className: styles.heycarSlider,
      max: stepCount - 1,
      min: 0,
      step: step,
      minDistance: minDistance,
      pearling: true,
      disabled: disabled,
      renderThumb: (p: Record<string, any>, state: ValueState) => (
        <div {...p} key={p.key}>
          {isWithHistogram && (
            <Typography Component="span" className="tooltip" variant="caption5">
              {typeof ariaValueText === 'function'
                ? ariaValueText(state)
                : ariaValueText
                  ? ariaValueText
                  : ''}
              {state.valueNow}
            </Typography>
          )}
        </div>
      ),
      thumbActiveClassName: 'dragging',
      thumbClassName: 'range-slider__thumb',
    };

    return (
      <div
        className={`${styles.sliderWrapper} ${
          isWithHistogram ? styles.isWithHistogram : ''
        } ${className} ${disabled ? styles.disabled : ''}`}
        data-test-id={dataTestId}
        ref={ref}
        {...rest}
      >
        {isSingleKnob ? (
          <ReactSlider
            ariaLabel={locale.thumb1}
            {...commonSliderProps}
            onAfterChange={(values, thumbIndex) =>
              singleKnobAfterChange(values, thumbIndex, onAfterChange)
            }
            onChange={(value, thumbIndex) => singleKnobChange(value, thumbIndex, onChange)}
            trackClassName={`range-slider__track ${isSingleKnob ? 'single-knob-track' : ''}`}
            value={selectedRangeIndexes ? selectedRangeIndexes[0] : 0}
          />
        ) : (
          <ReactSlider
            ariaLabel={[locale.thumb1, locale.thumb2]}
            {...commonSliderProps}
            onAfterChange={(values: number[], thumbIndex: number) => {
              removeDraggingClass();
              if (onAfterChange) onAfterChange([values[0], values[1]], thumbIndex);
            }}
            onChange={(value, thumbIndex) => onChange?.([value[0], value[1]], thumbIndex)}
            trackClassName="range-slider__track"
            value={selectedRangeIndexes || [0, stepCount - 1]}
          />
        )}
      </div>
    );
  },
);

Slider.displayName = 'Slider';
export default Slider;
